import { IReduxEnumType } from "@multiweb/reactCore/enums";
import { ExtendedLocalizableOption } from "@multiweb/reactCore/i18n";
import { ComboBoxOption, ComboBoxValues } from "../../ComboBoxComponent";

/**
 * Na základě `multiEditOptionText` vytvoří option pro položku multieditace.
 * @param multiEditOptionText přiznak, zda je aktivní multieditace
 */
export const getMultiEditOption = (multiEditOptionText: string): ComboBoxOption => ({
    value: ComboBoxValues.multiEditOptionValue,
    text: multiEditOptionText,
});

/**
 * Na základě `placeholderText` vytvoří option pro položku placeholderu. Tato option je defaultně skrytá.
 * Pokud je `placeholderText` definován, možnost obsahuje i lokalizovaný text.
 * @param placeholderText lokalizační klíč, na jehož základě bude option obsahovat i lokalizovaný text
 */
export const getPlaceholderOption = (placeholderText?: string): ComboBoxOption => {
    let placeholderOption: ComboBoxOption;
    if (placeholderText) {
        placeholderOption = {
            value: ComboBoxValues.placeholderOptionValue,
            text: placeholderText,
            hide: true,
            isPlaceholder: true,
        };
    } else {
        placeholderOption = {
            value: ComboBoxValues.placeholderOptionValue,
            text: "",
            hide: true,
            isPlaceholder: true,
        };
    }

    return placeholderOption;
};

/**
 * Vrací prázdnou option bez textu. Na základě přiznáků `isEditing` a `required` se urči, zdá má být option skrytá.
 * @param isEditing přiznak, zda je aktivní režim editace
 * @param required přiznak, zda je combobox povinný
 */
export const getEmptyOption = (isEditing?: boolean, required?: boolean): ComboBoxOption => ({
    value: ComboBoxValues.emptyOptionValue,
    text: "",
    hide: isEditing && required,
});

/**
 * Na základě `enumType` vrací pole options k lokalizaci s meta daty v podobě jména položky enumu.
 * @param enumType typ enumu
 */
export const getExtendedLocalizedOptions = (enumType: IReduxEnumType): ReadonlyArray<ExtendedLocalizableOption<number, string>> => (
    enumType.descriptions.map(desc => ({
        value: desc.intValue,
        locKey: desc.locKey,
        meta: desc.name,
    }))
);

/**
 * Obohatí na základě `hideValues` relevantní optiony přiznakem hide=true. Jednotlivé položky pole projde a pokud jsou definovány v řetězci hideValues, nastaví je pro skrytí.
 * Položky v poli nemaže, vrací je všechny.
 *
 * Př. HideValues="Neuvedeno;Sklad;Nakladove_stredisko"
 * @param options položky pro combobox
 * @param hideValues řetězec položek, které se nemají zobrazovat v comboboxu
 */
export const updateOptionsWithHidden = (options: ReadonlyArray<ComboBoxOption>, hideValues?: string): ReadonlyArray<ComboBoxOption> => {
    if (!hideValues) {
        return options;
    }

    const optionsWithHidden: ComboBoxOption[] = [];

    // Parsing dat z řetězce hideValues
    // odstranime případné mezery okolo středníku a vymžeme prázdné prvky z pole
    const hideValArr: string[] = hideValues.split(/\s*;\s*/).filter(Boolean);

    // Na základě pole hideValArr přidá do relevantních options k atributu hide hodnotu true. Všechny položky následně vrátí.
    options.forEach(option => {
        const hiddenFound = hideValArr.find(hideVal => hideVal === option.meta);
        const updatedOption: ComboBoxOption = {
            value: option.value,
            text: option.text,
            hide: option.hide,
            meta: option.meta,
        };
        if (hiddenFound) {
            updatedOption.hide = true;
        }
        optionsWithHidden.push(updatedOption);
    });

    return optionsWithHidden;
};
